import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Shield, AlertTriangle, Trophy, Music, Building, Users, GraduationCap, Layers, ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import AppDownload from '../components/home/AppDownload';
import { heroContent, valueBlocks, eventTypes, aboutContent } from '../data/mock';
// Infografía del About y logos de clientes: assets locales del repo.
import { soemServicios as aboutImage, clientLogos } from '../assets';
import '../styles/home.css';
import usePageMeta from '../hooks/usePageMeta';

const iconMap = {
  Search, Shield, AlertTriangle, Trophy, Music, Building, Users, GraduationCap, Layers
};

// Imágenes únicas de eventos - cada una diferente
const eventImages = {
  1: "https://images.unsplash.com/photo-1767584412923-f66fb341a2ce?w=800&q=80", // Deportivos - Estadio
  2: "https://images.unsplash.com/photo-1576514129883-2f1d47a65da6?w=800&q=80", // Conciertos - Escenario
  3: "https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?w=800&q=80", // Corporativos - Sala ejecutiva
  4: "https://images.unsplash.com/photo-1553545894-cf4d7f2bf8ac?w=800&q=80", // Convenciones - Exposición
  5: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&q=80", // Académicos - Graduación
  6: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800&q=80", // Masivos - Festival
};



const Home = () => {
  usePageMeta({
    title: null,
    description: 'Consultoría especializada en seguridad para eventos de alto nivel: planeación, evaluación y gestión de riesgos con metodología documentada y enfoque preventivo.',
    path: '/',
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-glow hero-glow-1"></div>
        <div className="hero-glow hero-glow-2"></div>
        <div className="hero-glow hero-glow-3"></div>
        <div className="container">
          <div className="hero-content">
            <span className="hero-tag">Consultoría Especializada</span>
            <h1 className="hero-title">
              Seguridad <span>estratégica</span> para eventos de alto nivel
            </h1>
            <p className="hero-subtitle">
              {heroContent.subtitle}
            </p>
            <div className="hero-cta">
              <Link to="/contact" className="btn btn-primary">
                Solicitar información
                <ArrowRight size={18} />
              </Link>
              <Link to="/methodology" className="btn btn-outline">
                Conocer metodología
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Blocks */}
      <section className="section value-blocks">
        <div className="container">
          <div className="value-grid">
            {valueBlocks.map((block) => {
              const IconComponent = iconMap[block.icon];
              return (
                <div key={block.id} className="card value-card">
                  <div className="value-icon">
                    {IconComponent && <IconComponent size={28} />}
                  </div>
                  <h3>{block.title}</h3>
                  <p>{block.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section about-preview">
        <div className="container">
          <div className="about-grid">
            <div className="about-image">
              <div className="about-image-box has-image">
                {/* width/height son las dimensiones intrínsecas del archivo:
                    con ellas el navegador reserva el espacio ANTES de cargar
                    la imagen. Sin esto y con loading="lazy", la caja
                    colapsaba a 0 px de alto hasta que la imagen llegaba
                    (y provocaba salto de layout al aparecer). */}
                <img
                  src={aboutImage}
                  width={853}
                  height={1280}
                  alt="SOEM Partners Group — Cultura en seguridad, experiencia en protección. Servicios: Consulting, Training, Operation, Movility y Production. Eventos masivos, corporativos, deportivos, culturales y musicales."
                  loading="lazy"
                />
              </div>
            </div>
            <div className="about-content">
              <h2>Qué es SOEM Partners Group</h2>
              <p>{aboutContent.philosophy}</p>
              <p>No somos seguridad privada tradicional. Somos estrategia, planeación y criterio técnico documentado al servicio de eventos que requieren el más alto estándar de protección.</p>
              <Link to="/about" className="btn btn-primary">
                Conocer más
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="section event-types">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Tipos de Eventos</h2>
            <p className="section-subtitle">
              Experiencia en eventos de diversa naturaleza y complejidad
            </p>
          </div>
          <div className="event-grid-images">
            {eventTypes.map((event) => {
              const IconComponent = iconMap[event.icon];
              return (
                <div key={event.id} className="event-card-image" style={{backgroundImage: `url(${eventImages[event.id]})`}}>
                  <div className="event-card-overlay"></div>
                  <div className="event-card-content">
                    <div className="event-icon">
                      {IconComponent && <IconComponent size={24} />}
                    </div>
                    <h4>{event.name}</h4>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="section clients-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Nuestros Clientes</h2>
            <p className="section-subtitle">
              Empresas e instituciones que confían en nuestra experiencia
            </p>
          </div>
          <div className="clients-logo-grid">
            {clientLogos.map((client, index) => (
              <div key={index} className="client-logo-card">
                <img src={client.url} alt={client.name} title={client.name} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Descarga de la app móvil */}
      <AppDownload />

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>¿Tiene un evento próximo?</h2>
            <p>
              Contáctenos para una evaluación inicial sin compromiso. 
              Nuestro equipo analizará sus necesidades y le presentará una propuesta personalizada.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Solicitar información
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
